import { createRoot } from 'react-dom/client';
import { RenderErrorBoundary } from './RenderErrorBoundary.jsx';
import { App } from './App.jsx';
import { SidebarApp } from './SidebarApp.jsx';
import { ListingApp } from './Listing.jsx';
import { FileCollectionWidget, readFileWidgetSettings } from './FileWidget.jsx';
import { FileViewer, isViewableFile } from './FileViewer.jsx';
import { mountReactSelect } from './AjaxSelect.jsx';
import { readJsonScript } from './utils.js';
import './styles.css';

console.log('[cst_admin_tools] react crud bundle loaded');
window.CstAdminToolsReactCrudBundleLoaded = true;

let globalViewerRoot = null;
let globalViewerEl = null;

function mountFileViewer(url, filename) {
  if (!isViewableFile(url)) return;
  if (!globalViewerEl) {
    globalViewerEl = document.createElement('div');
    globalViewerEl.id = 'cat-global-file-viewer';
    document.body.appendChild(globalViewerEl);
  }
  if (!globalViewerRoot) {
    globalViewerRoot = createRoot(globalViewerEl);
  }
  globalViewerRoot.render(
    <FileViewer
      url={url}
      filename={filename || ''}
      onClose={() => globalViewerRoot.render(null)}
    />,
  );
}

const mountedRoots = new WeakMap();

function readCrudSettings(element) {
  const schemaUrl = element.dataset.schemaUrl;
  if (!schemaUrl) {
    return null;
  }

  return {
    schemaUrl,
    layout: element.dataset.layout === 'sidebar' ? 'sidebar' : 'page',
  };
}

function mountElement(element) {
  const settings = readCrudSettings(element);
  if (!element || !settings) {
    return;
  }

  let root = mountedRoots.get(element);
  if (!root) {
    root = createRoot(element);
    mountedRoots.set(element, root);
  }

  root.render(
    <RenderErrorBoundary message="React CRUD page failed to render.">
      <App settings={settings} />
    </RenderErrorBoundary>,
  );
}

function mountSidebarElement(element) {
  if (!element) {
    return;
  }

  let root = mountedRoots.get(element);
  if (!root) {
    root = createRoot(element);
    mountedRoots.set(element, root);
  }

  root.render(<SidebarApp element={element} />);
}

function readListingSettings(element) {
  return readJsonScript(element, '[data-cst-react-listing-data]');
}

function mountListingElement(element) {
  const payload = readListingSettings(element);
  if (!element || !payload) {
    return;
  }

  let root = mountedRoots.get(element);
  if (!root) {
    root = createRoot(element);
    mountedRoots.set(element, root);
  }

  root.render(
    <RenderErrorBoundary message="React CRUD listing failed to render.">
      <ListingApp payload={payload} />
    </RenderErrorBoundary>,
  );
}

function mountFileWidgetHost(hostEl) {
  const settings = readFileWidgetSettings(hostEl);
  if (!hostEl || !settings) {
    return;
  }

  let root = mountedRoots.get(hostEl);
  if (!root) {
    root = createRoot(hostEl);
    mountedRoots.set(hostEl, root);
  }

  root.render(
    <RenderErrorBoundary message="React file widget failed to render.">
      <FileCollectionWidget hostEl={hostEl} />
    </RenderErrorBoundary>,
  );
}

function mount(context = document) {
  const elements = [];
  const listingElements = [];
  const sidebarElements = [];
  const fileWidgetHosts = [];
  if (context instanceof HTMLElement && context.matches('[data-cst-react-crud-root]')) {
    elements.push(context);
  }
  if (context instanceof HTMLElement && context.matches('[data-cst-react-listing-root]')) {
    listingElements.push(context);
  }
  if (context instanceof HTMLElement && context.matches('[data-cst-react-sidebar-root]')) {
    sidebarElements.push(context);
  }
  if (context instanceof HTMLElement && context.matches('[data-cst-file-widget-host="1"]')) {
    fileWidgetHosts.push(context);
  }

  if (context.querySelectorAll) {
    context.querySelectorAll('[data-cst-react-crud-root]').forEach((element) => {
      elements.push(element);
    });
    context.querySelectorAll('[data-cst-react-listing-root]').forEach((element) => {
      listingElements.push(element);
    });
    context.querySelectorAll('[data-cst-react-sidebar-root]').forEach((element) => {
      sidebarElements.push(element);
    });
    context.querySelectorAll('[data-cst-file-widget-host="1"]').forEach((element) => {
      fileWidgetHosts.push(element);
    });
    context.querySelectorAll('select.en-select2[data-ajax-manager][data-ajax-mode]').forEach((el) => {
      mountReactSelect(el);
    });
  }
  if (context instanceof HTMLElement && context.matches('select.en-select2[data-ajax-manager][data-ajax-mode]')) {
    mountReactSelect(context);
  }

  const seen = new Set();
  elements.forEach((element) => {
    if (seen.has(element)) {
      return;
    }

    seen.add(element);
    mountElement(element);
  });

  const seenListings = new Set();
  listingElements.forEach((element) => {
    if (seenListings.has(element)) {
      return;
    }

    seenListings.add(element);
    mountListingElement(element);
  });

  const seenSidebars = new Set();
  sidebarElements.forEach((element) => {
    if (seenSidebars.has(element)) {
      return;
    }

    seenSidebars.add(element);
    mountSidebarElement(element);
  });

  const seenFileWidgets = new Set();
  fileWidgetHosts.forEach((element) => {
    if (seenFileWidgets.has(element)) {
      return;
    }

    seenFileWidgets.add(element);
    mountFileWidgetHost(element);
  });
}

const DrupalApi = window.Drupal || {};
DrupalApi.behaviors = DrupalApi.behaviors || {};
DrupalApi.behaviors.cstAdminToolsReactCrud = {
  attach(context) {
    mount(context);
  },
};
window.Drupal = DrupalApi;
window.CstAdminToolsReactCrud = {
  mount,
  mountElement,
  mountListingElement,
  mountSidebarElement,
  mountFileWidgetHost,
  mountFileViewer,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
} else {
  mount();
}
